import React from 'react';
import { Form } from 'react-bootstrap';
import { positionKeyFacts } from '@/shared/utils/positionKeyFacts';
import { positionHeading } from '@/shared/utils/positionText';
import { trainingLine } from '@/shared/utils/trainingSessions';
import { commitmentLabel } from '@/shared/utils/requirementAnswers';

export const commitmentId = (position) => `sap-commitment-${position}`;

const rowOf = (position, context) =>
    context?.positionRows?.[position.applicationId] || position.summary;

// The positions the student chose that are still unconfirmed. A student
// cannot submit until each chosen position's times and hours are confirmed.
export function getErrors(answers) {
    const positions = answers.positions || [];
    const confirmed = answers.requirementAnswers?.commitments || {};
    return positions
        .map((position, index) => ({ position, index }))
        .filter(({ position }) => confirmed[position.applicationId] !== true)
        .map(({ position, index }) => {
            const row = rowOf(position, answers);
            return {
                id: commitmentId(position.applicationId),
                label: row
                    ? `Confirm the times and hours for ${positionHeading(row)}`
                    : `Confirm the times and hours for choice ${index + 1}`,
            };
        });
}

/**
 * Each chosen position with when it meets, where, and the hours a week the
 * program sets, and a checkbox the student ticks to confirm they can do it.
 * Replaces asking the student for their own hours a week: the commitment is
 * the position's. Nothing is asked of an application with no chosen position
 * (the general pool keeps its own availability question). `single` is for a
 * screen that asks about one position it already knows, a nomination: no
 * choice numbers and no talk of several positions.
 */
function PositionCommitments({
    answers,
    onChange,
    errorIds,
    context,
    single = false,
}) {
    const positions = answers.positions || [];
    if (positions.length === 0) return null;
    const current = answers.requirementAnswers;
    const training = trainingLine(
        context?.trainingSessions,
        answers.programAnswers?.isReturning === true
    );

    return (
        <section aria-labelledby="sap-commitments-heading" className="mb-3">
            <h2 className="h5 mb-2" id="sap-commitments-heading">
                Meeting times and hours
            </h2>
            <p className="text-muted small">
                {single
                    ? 'Confirm you can work this position.'
                    : 'Confirm each position you could accept. Selecting several positions does not mean you will work all of them.'}
            </p>
            {training && <p className="small">{training}</p>}
            {positions.map((position, index) => {
                const row = rowOf(position, context);
                const id = commitmentId(position.applicationId);
                const invalid = Boolean(errorIds?.has(id));
                const heading = row
                    ? positionHeading(row)
                    : `Choice ${index + 1}`;
                return (
                    <div
                        key={position.applicationId}
                        className="border rounded p-3 mb-3"
                    >
                        <h3 className="h6 mb-2">
                            {single
                                ? heading
                                : `Choice ${index + 1}: ${heading}`}
                        </h3>
                        <p className="mb-2">
                            {row
                                ? positionKeyFacts(row, { notePlace: true })
                                : 'Meeting times not set yet'}
                        </p>
                        <Form.Check
                            type="checkbox"
                            id={id}
                            label={commitmentLabel(row?.hoursPerWeek)}
                            checked={
                                current.commitments?.[
                                    position.applicationId
                                ] === true
                            }
                            isInvalid={invalid}
                            aria-invalid={invalid}
                            aria-describedby={
                                invalid ? `${id}-error` : undefined
                            }
                            onChange={(e) =>
                                onChange('requirementAnswers', {
                                    ...current,
                                    commitments: {
                                        ...current.commitments,
                                        [position.applicationId]:
                                            e.target.checked,
                                    },
                                })
                            }
                        />
                        {invalid && (
                            <div
                                id={`${id}-error`}
                                role="alert"
                                className="invalid-feedback d-block"
                            >
                                {`Confirm you can attend ${heading} and commit the hours a week.`}
                            </div>
                        )}
                    </div>
                );
            })}
        </section>
    );
}

export default PositionCommitments;
